import React, { useEffect, useState } from 'react';

import useToggle from '../../../../hooks/useToggle';
import { useAppDispatch, useToolkitDispatch } from '../../../../hooks';

import { hideGlobalDialog } from '../../../../state/ui/dialog';

import { Dialog, DialogContent, IconButton } from '@mui/material';
import DialogTitle from '../../../../components/dialogs/DialogTitle';
import LiveViewPlayers from '../../../../features/LiveView/components/LiveViewPlayers';
import PlayerMessage from '../../../../features/LiveView/components/PlayerMessage';
import {
    fetchActiveLiveCameras,
    resetLiveViewState,
    startLiveView,
} from '../../../../features/LiveView/state';

import { useStyles } from './Themable.hooks';
import RefreshIcon from '@mui/icons-material/Refresh';
import useAsyncThunkWithSnackbar from '../../../../hooks/useAsyncThunkWithSnackbar';

interface IOwnProps {
    monitoredId: string;
}

const LiveViewDialog = ({ monitoredId }: IOwnProps) => {
    const dispatch = useAppDispatch();
    const toolkitDispatch = useToolkitDispatch();
    const handleAsyncRequest = useAsyncThunkWithSnackbar();

    const classes = useStyles();
    const { isOpen, handleToggle } = useToggle(true);

    const [error, setError] = useState(false);

    const resetData = () => {
        setError(false);
        handleAsyncRequest({
            asyncAction: startLiveView(monitoredId),
            onError: { callback: () => setError(true) },
        });
    };

    useEffect(() => {
        if (monitoredId) {
            handleAsyncRequest({
                asyncAction: startLiveView(monitoredId),
                onError: { callback: () => setError(true) },
            });

            const timeoutId = setInterval(() => {
                toolkitDispatch(fetchActiveLiveCameras(monitoredId))
                    .unwrap()
                    .then(() => clearInterval(timeoutId))
                    .catch((error) => {
                        if (error.status !== 404) {
                            clearInterval(timeoutId);
                        }
                    });
            }, 2000);

            return () => {
                clearInterval(timeoutId);
            };
        }
        return;
    }, [monitoredId, toolkitDispatch, handleAsyncRequest]);

    const handleClose = (event?: object, reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        dispatch(hideGlobalDialog());
        toolkitDispatch(resetLiveViewState());
        handleToggle();
    };

    const renderContent = (monitoredId: string, error: boolean) => {
        if (error) {
            return (
                <PlayerMessage message="Live view could not be loaded">
                    <IconButton onClick={resetData}>
                        <RefreshIcon />
                    </IconButton>
                </PlayerMessage>
            );
        }

        return <LiveViewPlayers monitoredId={monitoredId} />;
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullWidth={true}
            maxWidth={'lg'}
        >
            <DialogTitle title={'Live view'} close={handleClose} />
            <DialogContent className={classes.dialogContent}>
                {renderContent(monitoredId, error)}
            </DialogContent>
        </Dialog>
    );
};

export default LiveViewDialog;
